import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Chip,
  Link,
} from '@mui/material';
import http from '../../services/http';
import {
  isoToDatetimeLocalValue,
  datetimeLocalToIso,
  formatToDatetimeLocal,
  allDayLocalRangeToIsoFromDatetimeLocal,
  allDayFormStringsFromIso,
} from '../../utils/calendarUtils';
import { toast } from 'react-toastify';

const emptyForm = () => ({
  title: '',
  description: '',
  startsLocal: '',
  endsLocal: '',
  all_day: false,
  visibility: 'private',
  ticket_id: null,
  attendee_user_ids: [],
  attendee_emails: [],
  attendee_group_ids: [],
  remind_minutes: 30,
  remind_popup: true,
  remind_email: false,
});

const CalendarEventDialog = ({
  open,
  onClose,
  onSaved,
  event,
  users = [],
  currentUserId,
  defaultTicketId = null,
  // Numero por tenant, so para o rotulo: o vinculo gravado usa
  // defaultTicketId, que continua sendo a PK.
  defaultTicketNumber = null,
  readOnly = false,
  initialSlotStart = null,
  initialSlotEnd = null,
}) => {
  const history = useHistory();

  /*
   * O vínculo REAL do compromisso tem precedência sobre o padrão: ao editar
   * pela agenda, o evento traz o próprio ticket em `event.ticket`.
   *
   * O par `defaultTicketId`/`defaultTicketNumber` serve ao compromisso NOVO,
   * criado de dentro do chamado -- ele ainda não existe, e por isso não tem
   * `event` de onde tirar o vínculo.
   */
  const ticketDoCompromisso = event?.ticket?.id
    ? event.ticket
    : (defaultTicketId ? { id: defaultTicketId, number: defaultTicketNumber, title: null } : null);
  const [form, setForm] = useState(emptyForm);
  const [serviceGroups, setServiceGroups] = useState([]);
  // Texto ainda não "chipado" no campo de e-mail. Guardamos para não perder o
  // e-mail digitado quando o usuário salva sem teclar Enter.
  const [emailInput, setEmailInput] = useState('');

  // Grupos de atendimento para convidar a equipe inteira de uma vez (item 7).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    http.get('/groups')
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setServiceGroups(list.filter((g) => g && (g.status === true || g.status === 1)));
      })
      .catch(() => { if (!cancelled) setServiceGroups([]); });
    return () => { cancelled = true; };
  }, [open]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmailInput('');
    if (event) {
      const allDay = !!event.all_day;
      const { startsLocal, endsLocal } = allDay
        ? allDayFormStringsFromIso(event.starts_at)
        : {
            startsLocal: isoToDatetimeLocalValue(event.starts_at),
            endsLocal: isoToDatetimeLocalValue(event.ends_at),
          };
      setForm({
        title: event.title || '',
        description: event.description || '',
        startsLocal,
        endsLocal,
        all_day: allDay,
        visibility: event.visibility === 'public' ? 'public' : 'private',
        ticket_id: event.ticket_id ?? null,
        attendee_user_ids: Array.isArray(event.attendee_user_ids) ? [...event.attendee_user_ids] : [],
        attendee_emails: Array.isArray(event.attendee_emails) ? [...event.attendee_emails] : [],
        attendee_group_ids: Array.isArray(event.attendee_group_ids) ? [...event.attendee_group_ids] : [],
        remind_minutes: event.remind_minutes ?? null,
        remind_popup: event.remind_popup !== false,
        remind_email: event.remind_email === true,
      });
    } else {
      const base = emptyForm();
      if (defaultTicketId) {
        base.ticket_id = defaultTicketId;
      }
      if (initialSlotStart && initialSlotEnd) {
        base.startsLocal = isoToDatetimeLocalValue(initialSlotStart);
        base.endsLocal = isoToDatetimeLocalValue(initialSlotEnd);
      } else {
        const now = new Date();
        now.setMinutes(0, 0, 0);
        const end = new Date(now.getTime() + 60 * 60 * 1000);
        base.startsLocal = formatToDatetimeLocal(now);
        base.endsLocal = formatToDatetimeLocal(end);
      }
      setForm(base);
    }
  }, [open, event, defaultTicketId, initialSlotStart, initialSlotEnd]);

  const attendeeOptions = (users || []).filter((u) => u.id !== currentUserId);
  const selectedAttendees = attendeeOptions.filter((u) => form.attendee_user_ids.includes(u.id));

  const isOwner = !event || Number(event.user_id) === Number(currentUserId);
  const disabled = readOnly || (event && !isOwner);

  const handleAllDay = (checked) => {
    setForm((prev) => {
      if (!checked) return { ...prev, all_day: false };
      let base = prev.startsLocal ? new Date(prev.startsLocal) : new Date();
      if (Number.isNaN(base.getTime())) base = new Date();
      const y = base.getFullYear();
      const m = base.getMonth();
      const d = base.getDate();
      const dayStart = new Date(y, m, d, 0, 0, 0, 0);
      const dayEnd = new Date(y, m, d, 23, 59, 59, 999);
      return {
        ...prev,
        all_day: true,
        startsLocal: formatToDatetimeLocal(dayStart),
        endsLocal: formatToDatetimeLocal(dayEnd),
      };
    });
  };

  // Divide o texto digitado em e-mails (aceita vírgula, ponto-e-vírgula ou espaço).
  const splitEmails = (raw) =>
    String(raw || '')
      .split(/[\s,;]+/)
      .map((e) => e.trim())
      .filter(Boolean);

  // Move o e-mail digitado para os chips (usado no onBlur e ao teclar separador),
  // evitando que o valor se perca por falta do Enter.
  const commitEmails = (raw) => {
    const tokens = splitEmails(raw);
    if (tokens.length) {
      setForm((p) => ({
        ...p,
        attendee_emails: Array.from(new Set([...(p.attendee_emails || []), ...tokens])),
      }));
    }
    setEmailInput('');
  };

  const submit = async () => {
    if (!form.title?.trim()) {
      toast.warning('Informe o título.');
      return;
    }
    let startsAt;
    let endsAt;
    if (form.all_day) {
      const range = allDayLocalRangeToIsoFromDatetimeLocal(form.startsLocal);
      startsAt = range.startsAt;
      endsAt = range.endsAt;
    } else {
      startsAt = datetimeLocalToIso(form.startsLocal);
      endsAt = datetimeLocalToIso(form.endsLocal);
    }
    if (!startsAt || !endsAt) {
      toast.warning('Informe início e fim.');
      return;
    }
    if (new Date(endsAt) <= new Date(startsAt)) {
      toast.warning('O fim deve ser após o início.');
      return;
    }

    // Inclui qualquer e-mail ainda digitado no campo (sem Enter) no envio.
    const allEmails = Array.from(
      new Set(
        [...(form.attendee_emails || []), ...splitEmails(emailInput)]
          .map((e) => String(e).trim())
          .filter(Boolean)
      )
    );

    const payload = {
      title: form.title.trim(),
      description: form.description?.trim() || null,
      starts_at: startsAt,
      ends_at: endsAt,
      all_day: form.all_day,
      visibility: form.visibility,
      ticket_id: form.ticket_id || null,
      attendee_user_ids: form.attendee_user_ids,
      attendee_emails: allEmails,
      attendee_group_ids: form.attendee_group_ids || [],
      remind_minutes: form.remind_minutes === '' ? null : form.remind_minutes,
      remind_popup: form.remind_popup,
      remind_email: form.remind_email,
    };

    if (event?.source === 'ticket_sla') {
      return;
    }
    setSaving(true);
    try {
      if (event?.id && typeof event.id === 'number') {
        await http.put(`/calendar-events/${event.id}`, payload);
        toast.success('Evento atualizado.');
      } else {
        await http.post('/calendar-events', payload);
        toast.success('Evento criado.');
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      const msg = err.response?.data?.message || 'Erro ao salvar evento.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!event?.id || event?.source === 'ticket_sla' || !isOwner) return;
    if (!window.confirm('Excluir este evento?')) return;
    setSaving(true);
    try {
      await http.delete(`/calendar-events/${event.id}`);
      toast.success('Evento excluído.');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao excluir.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {readOnly ? 'Compromisso' : event ? 'Editar compromisso' : 'Novo compromisso'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
          {/*
            * O chamado do compromisso (ponto 3 da Gisele, 15/10/2026).
            *
            * Antes isto só aparecia quando o compromisso era CRIADO de dentro
            * do chamado, porque dependia de `defaultTicketId` -- e a agenda
            * passa `null`. Ao editar pela agenda, o vínculo existia no banco e
            * sumia da tela.
            *
            * Agora o evento manda o ticket junto (`event.ticket`), e ele tem
            * precedência: é o vínculo REAL daquele compromisso. O
            * `defaultTicket*` continua valendo para o compromisso NOVO, que
            * ainda não existe e por isso não tem `event`.
            *
            * E virou link: saber o número sem poder chegar nele é metade do
            * caminho. Fecha o diálogo antes de navegar -- deixá-lo aberto por
            * cima da tela do chamado seria um modal órfão.
            */}
          {ticketDoCompromisso && (
            <Typography variant="caption" color="text.secondary">
              Vinculado ao chamado{' '}
              <Link
                component="button"
                type="button"
                data-testid="link-do-chamado"
                onClick={() => {
                  onClose();
                  history.push(`/ticket/${ticketDoCompromisso.id}`);
                }}
                sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}
              >
                #{ticketDoCompromisso.number}
              </Link>
              {ticketDoCompromisso.title ? ` — ${ticketDoCompromisso.title}` : ''}
            </Typography>
          )}
          <TextField
            label="Título"
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            fullWidth
            required
            disabled={disabled}
            size="small"
          />
          <TextField
            label="Descrição"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
            disabled={disabled}
            size="small"
          />
          <FormControlLabel
            control={
              <Checkbox checked={form.all_day} onChange={(e) => handleAllDay(e.target.checked)} disabled={disabled} />
            }
            label="Dia inteiro"
          />
          <TextField
            label="Início"
            type={form.all_day ? 'date' : 'datetime-local'}
            value={
              form.all_day && form.startsLocal
                ? form.startsLocal.slice(0, 10)
                : form.startsLocal
            }
            onChange={(e) => {
              const v = e.target.value;
              if (form.all_day) {
                const [yy, mm, dd] = v.split('-').map((x) => parseInt(x, 10));
                const dayStart = new Date(yy, mm - 1, dd, 0, 0, 0, 0);
                const dayEnd = new Date(yy, mm - 1, dd, 23, 59, 59, 999);
                setForm((p) => ({
                  ...p,
                  startsLocal: formatToDatetimeLocal(dayStart),
                  endsLocal: formatToDatetimeLocal(dayEnd),
                }));
              } else {
                setForm((p) => ({ ...p, startsLocal: v }));
              }
            }}
            fullWidth
            disabled={disabled}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          {!form.all_day && (
            <TextField
              label="Fim"
              type="datetime-local"
              value={form.endsLocal}
              onChange={(e) => setForm((p) => ({ ...p, endsLocal: e.target.value }))}
              fullWidth
              disabled={disabled}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          )}
          <TextField
            select
            label="Visibilidade"
            value={form.visibility}
            onChange={(e) => setForm((p) => ({ ...p, visibility: e.target.value }))}
            fullWidth
            disabled={disabled}
            size="small"
            helperText="Público: outros usuários veem na agenda do ticket e na sua agenda compartilhada."
          >
            <MenuItem value="private">Privado (só eu e convidados)</MenuItem>
            <MenuItem value="public">Público</MenuItem>
          </TextField>
          <Autocomplete
            multiple
            options={attendeeOptions}
            getOptionLabel={(o) => o.name || o.email || String(o.id)}
            value={selectedAttendees}
            onChange={(_, v) =>
              setForm((p) => ({ ...p, attendee_user_ids: v.map((u) => u.id) }))
            }
            disabled={disabled}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Convidar (notificação)" size="small" placeholder="Operadores" />
            )}
          />
          <Autocomplete
            multiple
            options={serviceGroups}
            getOptionLabel={(o) => o.description || o.name || String(o.id)}
            value={serviceGroups.filter((g) => (form.attendee_group_ids || []).includes(g.id))}
            onChange={(_, v) => setForm((p) => ({ ...p, attendee_group_ids: v.map((g) => g.id) }))}
            disabled={disabled}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} label={option.description || option.name} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Convidar grupo de atendimento" size="small" placeholder="Ex.: Suporte (convida toda a equipe)" />
            )}
          />
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={form.attendee_emails || []}
            inputValue={emailInput}
            onInputChange={(_, v, reason) => {
              if (reason === 'reset') {
                setEmailInput('');
                return;
              }
              // Vírgula, ponto-e-vírgula ou espaço já viram chip na hora.
              if (/[\s,;]$/.test(v)) {
                commitEmails(v);
              } else {
                setEmailInput(v);
              }
            }}
            onChange={(_, v) =>
              setForm((p) => ({ ...p, attendee_emails: v.map((e) => String(e).trim()).filter(Boolean) }))
            }
            onBlur={() => commitEmails(emailInput)}
            disabled={disabled}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={`${option}-${index}`} label={option} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Convidar por e-mail" size="small" placeholder="Digite o e-mail e tecle Enter (ou vírgula)" />
            )}
          />
          <TextField
            select
            label="Lembrete"
            value={form.remind_minutes === null || form.remind_minutes === undefined ? '' : form.remind_minutes}
            onChange={(e) =>
              setForm((p) => ({ ...p, remind_minutes: e.target.value === '' ? null : Number(e.target.value) }))
            }
            fullWidth
            disabled={disabled}
            size="small"
          >
            <MenuItem value="">Sem lembrete</MenuItem>
            <MenuItem value={0}>No horário do compromisso</MenuItem>
            <MenuItem value={5}>5 minutos antes</MenuItem>
            <MenuItem value={10}>10 minutos antes</MenuItem>
            <MenuItem value={15}>15 minutos antes</MenuItem>
            <MenuItem value={30}>30 minutos antes</MenuItem>
            <MenuItem value={60}>1 hora antes</MenuItem>
            <MenuItem value={120}>2 horas antes</MenuItem>
            <MenuItem value={1440}>1 dia antes</MenuItem>
          </TextField>
          {form.remind_minutes !== null && form.remind_minutes !== undefined && form.remind_minutes !== '' && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pl: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={form.remind_popup}
                    onChange={(e) => setForm((p) => ({ ...p, remind_popup: e.target.checked }))}
                    disabled={disabled}
                  />
                }
                label="Pop-up no sistema"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={form.remind_email}
                    onChange={(e) => setForm((p) => ({ ...p, remind_email: e.target.checked }))}
                    disabled={disabled}
                  />
                }
                label="Alerta por e-mail"
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {event?.id && isOwner && (
          <Button color="error" onClick={handleDelete} disabled={saving}>
            Excluir
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        {!disabled && (
          <Button variant="contained" onClick={submit} disabled={saving}>
            Salvar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CalendarEventDialog;
